import {
	ApartmentOutlined,
	BankOutlined,
	EnvironmentOutlined,
	IdcardOutlined,
	MailOutlined,
	PhoneOutlined,
	TagOutlined,
	UserOutlined,
} from "@ant-design/icons";
import { Card, Col, Row, Skeleton, Tag, Typography } from "antd";
import { useOrganization } from "../hooks/useOrganizations";
import type { Organization } from "../types";

const { Text } = Typography;

interface OrgDetailInfoCardProps {
	org: Organization | undefined;
	isLoading: boolean;
}

const statusLabel = (org: Organization): string => {
	if (org.isLock) {
		return "Bloklangan";
	}
	if (org.isEducational) {
		return "Ta'lim muassasasi";
	}
	return "Faol";
};

const statusColor = (org: Organization): "error" | "success" | "processing" => {
	if (org.isLock) {
		return "error";
	}
	if (org.isEducational) {
		return "success";
	}
	return "processing";
};

// Requisite columns (TZ §3.1) — rendered only when the value is present
function OrgRequisiteCols({ org }: { org: Organization }) {
	return (
		<>
			{org.shortName && (
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<TagOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Qisqa nomi
							</Text>
							<Text className="font-medium">{org.shortName}</Text>
						</div>
					</div>
				</Col>
			)}
			{(org.leaderName || org.leaderPosition) && (
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<UserOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Rahbar
							</Text>
							<Text className="font-medium">{org.leaderName || "—"}</Text>
							{org.leaderPosition && (
								<Text type="secondary" className="text-xs block">
									{org.leaderPosition}
								</Text>
							)}
						</div>
					</div>
				</Col>
			)}
			{org.email && (
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<MailOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Email
							</Text>
							<Text className="font-medium">{org.email}</Text>
						</div>
					</div>
				</Col>
			)}
		</>
	);
}

export function OrgDetailInfoCard({ org, isLoading }: OrgDetailInfoCardProps) {
	// Parent name is not part of the detail payload — fetch it by parentId
	const { data: parentOrg, isLoading: isParentLoading } = useOrganization(
		org?.parentId ?? undefined
	);

	if (isLoading) {
		return (
			<Card className="mb-5 rounded-lg border-gray-100" size="small">
				<Skeleton active paragraph={{ rows: 2 }} />
			</Card>
		);
	}

	if (!org) {
		return null;
	}

	return (
		<Card className="mb-5 rounded-lg border-gray-100 shadow-sm" size="small">
			<Row gutter={[32, 16]}>
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<BankOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Tashkilot turi
							</Text>
							<Text className="font-medium">{org.type?.name ?? "—"}</Text>
						</div>
					</div>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<EnvironmentOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Hudud
							</Text>
							<Text className="font-medium">{org.soato?.nameUz ?? "—"}</Text>
						</div>
					</div>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<PhoneOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Telefon
							</Text>
							<Text className="font-medium">{org.phoneNumber || "—"}</Text>
						</div>
					</div>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<IdcardOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								STIR
							</Text>
							<Text className="font-medium">{org.stir || "—"}</Text>
						</div>
					</div>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<div className="flex items-start gap-2">
						<ApartmentOutlined className="text-gray-400 mt-0.5 text-sm" />
						<div>
							<Text type="secondary" className="text-xs block mb-0.5">
								Yuqori tashkilot
							</Text>
							{org.parentId && isParentLoading ? (
								<Skeleton.Input active size="small" style={{ width: 120 }} />
							) : (
								<Text className="font-medium">{parentOrg?.name ?? "—"}</Text>
							)}
						</div>
					</div>
				</Col>
				<Col xs={24} sm={12} md={6}>
					<div>
						<Text type="secondary" className="text-xs block mb-1">
							Holat
						</Text>
						<Tag color={statusColor(org)}>{statusLabel(org)}</Tag>
					</div>
				</Col>
				<OrgRequisiteCols org={org} />
			</Row>

			{org.address && (
				<div className="mt-4 pt-4 border-t border-gray-100">
					<Text type="secondary" className="text-xs block mb-0.5">
						Manzil
					</Text>
					<Text className="text-sm">{org.address}</Text>
				</div>
			)}
		</Card>
	);
}
